import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Slide, Typography, IconButton, Avatar, alpha } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import NotificationsRounded from '@mui/icons-material/NotificationsRounded';
import { useAppSelector, useAppDispatch } from '../../../hooks/useAppStore';
import { notificationsApi } from '../../../store/api/notificationsApi';
import { getPusher } from '../../../lib/pusher';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';

/**
 * Banner flotante arriba: aparece apenas llega un mensaje, estés donde estés en el CRM, y
 * se auto-oculta a los pocos segundos. Dos fuentes, la misma píldora:
 *
 *  - **Gustavo** (evento `gustavo.notice`, un proactivo): la foto de Gustavo, y al tocarlo
 *    abre su drawer (`gustavo:open`, que `GustavoWidget` ya escucha).
 *  - **Chat entre personas** (evento `message.new`: otro agente o Soporte Técnico, en el
 *    /chat): el avatar de quien escribe, y al tocarlo lleva a esa conversación.
 *  - **Cualquier otra notificación** (evento `notification.new`): las 44 de la campana —
 *    reservas, comisiones, retiros, cambios de cliente, captaciones por vencer, respuestas a
 *    un bug—, para todos los perfiles. Al tocarla lleva a su `link`. El backend las anuncia
 *    desde el modelo, así que ninguna se queda afuera. (pedido del user, 02/09/2026)
 *
 * ⛔ EL ACENTO ES EL DE LA PLATAFORMA, no un color fijo. El de chat estaba clavado en
 * `#6ea8fe` —el azul de Booster— y en Imperium el aviso salía celeste sobre un CRM dorado.
 * Sale de `CRM_PALETTE`, que es donde el tenant ya dejó pintado su acento.
 *
 * Reusa el canal compartido `private-user-{id}` (NO se hace unsubscribe: lo comparten el chat,
 * Phylax y los bugs — solo se desata el handler propio), el mismo camino que ya usaba el banner
 * de Gustavo. No me aviso de lo mío (echo del propio mensaje) ni cuando ya estoy en el /chat.
 */

const CUTOUT = '/gustavo-cutout.png';
const AUTO_DISMISS_MS = 7000;

type Aviso =
  | { kind: 'gustavo'; preview?: string }
  | { kind: 'chat'; nombre: string; avatar?: string; preview?: string; conversationId: string }
  | { kind: 'notif'; titulo: string; preview?: string; link?: string | null };

const iniciales = (nombre: string) =>
  nombre.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

export function MessageBanner() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /**
   * UN solo acento: el que el tenant dejó aplicado en la paleta del CRM. Se lee en render
   * —no al importar el módulo— porque el acento se pinta al arrancar y una captura a nivel
   * de módulo se queda con el color de fábrica.
   */
  const accent = P.brand.golden;

  useEffect(() => {
    if (!accessToken) return;
    let userId: string;
    try {
      userId = JSON.parse(atob(accessToken.split('.')[1])).sub as string;
    } catch {
      return;
    }

    const mostrar = (a: Aviso) => {
      setAviso(a);
      setVisible(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    };

    const pusher = getPusher(accessToken);
    const channel = pusher.subscribe(`private-user-${userId}`);

    const onNotice = (data: { preview?: string } = {}) =>
      mostrar({ kind: 'gustavo', preview: data?.preview });

    const onChat = (data: { conversationId?: string; message?: Record<string, any> } = {}) => {
      const m = data?.message;
      if (!m || !data.conversationId) return;
      const s = m.sender ?? {};
      // No me aviso de mi propio mensaje (llega por echo cuando lo mando desde otro lado).
      if (String(s._id ?? '') === userId) return;
      // Si ya estoy en el chat, el banner sobra: lo veo en la lista.
      if (window.location.pathname.startsWith('/chat')) return;
      const nombre = `${s.name ?? ''} ${s.lastName ?? ''}`.trim() || 'un compañero';
      const preview =
        m.type === 'image' ? '📷 Foto' : m.type === 'file' ? '📎 Archivo' : (m.content ?? '');
      mostrar({ kind: 'chat', nombre, avatar: s.avatar, preview, conversationId: data.conversationId });
    };

    /**
     * El resto de la campana. Se muestra tal cual la escribió quien la generó: `title` es
     * una frase completa ("Reserva confirmada · IMP-2026-2431"), así que no se le antepone
     * nada. Sin `link` la píldora igual sirve de aviso y lleva a la campana.
     */
    const onNotif = (data: { title?: string; body?: string; link?: string | null } = {}) => {
      if (!data?.title) return;
      mostrar({ kind: 'notif', titulo: data.title, preview: data.body || undefined, link: data.link ?? null });
      // Y la campana se pone al día sola: sin esto el aviso flotante decía que había algo
      // nuevo y el contador de al lado seguía en el número viejo hasta recargar.
      dispatch(notificationsApi.util.invalidateTags(['Notification']));
    };

    channel.bind('gustavo.notice', onNotice);
    channel.bind('message.new', onChat);
    channel.bind('notification.new', onNotif);
    return () => {
      channel.unbind('gustavo.notice', onNotice);
      channel.unbind('message.new', onChat);
      channel.unbind('notification.new', onNotif);
      clearTimeout(timer.current);
    };
  }, [accessToken, dispatch]);

  const abrir = () => {
    clearTimeout(timer.current);
    setVisible(false);
    if (aviso?.kind === 'chat') navigate(`/chat?conv=${aviso.conversationId}`);
    else if (aviso?.kind === 'notif') { if (aviso.link) navigate(aviso.link); }
    else window.dispatchEvent(new CustomEvent('gustavo:open'));
  };

  const cerrar = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearTimeout(timer.current);
    setVisible(false);
  };

  const esChat = aviso?.kind === 'chat';
  const esNotif = aviso?.kind === 'notif';
  const titulo = esNotif
    ? aviso!.titulo
    : esChat
      ? `Nuevo mensaje de ${aviso!.nombre}`
      : 'Tenés un nuevo mensaje de Gustavo';

  return (
    <Slide in={visible} direction="down" mountOnEnter unmountOnExit appear>
      <Paper
        onClick={abrir}
        role="alert"
        elevation={0}
        sx={{
          position: 'fixed',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: (t) => t.zIndex.snackbar + 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          pl: 1,
          pr: 0.5,
          py: 0.75,
          borderRadius: 999,
          cursor: 'pointer',
          width: 'max-content',
          maxWidth: 'min(92vw, 440px)',
          bgcolor: alpha('#0f1218', 0.97),
          color: '#fff',
          border: `1px solid ${alpha(accent, 0.38)}`,
          boxShadow: `0 14px 44px ${alpha('#000', 0.5)}, 0 0 0 1px ${alpha(accent, 0.08)}`,
          backdropFilter: 'blur(8px)',
          transition: 'border-color .15s ease',
          '&:hover': { borderColor: alpha(accent, 0.62) },
        }}
      >
        {esNotif ? (
          <Box
            sx={{
              width: 40, height: 40, flexShrink: 0, borderRadius: '50%',
              display: 'grid', placeItems: 'center',
              bgcolor: alpha(accent, 0.16),
              border: `1.5px solid ${alpha(accent, 0.5)}`,
            }}
          >
            <NotificationsRounded sx={{ fontSize: 21, color: accent }} />
          </Box>
        ) : esChat ? (
          <Avatar
            src={aviso!.avatar}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              fontSize: 15,
              fontWeight: 700,
              bgcolor: alpha(accent, 0.2),
              color: '#fff',
              border: `1.5px solid ${alpha(accent, 0.5)}`,
            }}
          >
            {iniciales(aviso!.nombre)}
          </Avatar>
        ) : (
          <Box
            component="img"
            src={CUTOUT}
            alt="Gustavo"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              objectFit: 'cover',
              objectPosition: 'top center',
              flexShrink: 0,
              bgcolor: alpha(accent, 0.12),
              border: `1.5px solid ${alpha(accent, 0.5)}`,
            }}
          />
        )}
        <Box sx={{ minWidth: 0, py: 0.25 }}>
          <Typography noWrap sx={{ fontWeight: 800, fontSize: 13.5, lineHeight: 1.2, color: accent, maxWidth: 300 }}>
            {titulo}
          </Typography>
          {aviso?.preview ? (
            <Typography noWrap sx={{ fontSize: 12, lineHeight: 1.3, color: alpha('#fff', 0.72), maxWidth: 300 }}>
              {aviso.preview}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, lineHeight: 1.3, color: alpha('#fff', 0.55) }}>
              Tocá para verlo
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          aria-label="Cerrar aviso"
          onClick={cerrar}
          sx={{ color: alpha('#fff', 0.45), ml: 0.25, '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) } }}
        >
          <CloseRounded sx={{ fontSize: 17 }} />
        </IconButton>
      </Paper>
    </Slide>
  );
}
